import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, Circle, Eye, Trash2, Upload } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi, pedir } from "@/api/cliente"
import { BotonArchivo } from "@/componentes/ui/BotonArchivo"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { comoFecha } from "@/lib/fechas"

import type { Documento, TiposDocumento } from "./tipos"

const ACEPTA = "application/pdf,image/png,image/jpeg,image/webp"

/**
 * Los documentos de una empresa, un chofer, un vehículo o un recibo del portal de
 * transporte (25/10): cada tipo con su estado —cargado o pendiente, y los obligatorios
 * marcados— y su botón de subir. Los archivos se acumulan; retirar no borra lo que ya
 * citaba un recibo emitido.
 */
export function Documentos({
  dueno,
  duenoId,
  base = "/api/v1/transporte",
  soloLectura = false,
}: {
  dueno: "EMPRESA" | "CHOFER" | "VEHICULO" | "RECIBO"
  duenoId: number
  base?: string
  soloLectura?: boolean
}) {
  const cliente = useQueryClient()
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const tipos = useQuery({
    queryKey: ["transporte", "tipos-documento"],
    queryFn: () => api.get<TiposDocumento>("/api/v1/transporte/tipos-documento"),
    enabled: !soloLectura,
    staleTime: Number.POSITIVE_INFINITY,
  })
  const clave = ["transporte", "documentos", dueno, duenoId]
  const docs = useQuery({
    queryKey: clave,
    queryFn: () => api.get<Documento[]>(`${base}/documentos?dueno=${dueno}&dueno_id=${duenoId}`),
  })
  const refrescar = () => {
    cliente.invalidateQueries({ queryKey: clave })
    cliente.invalidateQueries({ queryKey: ["transporte", "empresas"] })
  }
  const subir = useMutation({
    mutationFn: ({ tipo, archivo }: { tipo: string; archivo: File }) => {
      const datos = new FormData()
      datos.append("dueno", dueno)
      datos.append("dueno_id", String(duenoId))
      datos.append("tipo", tipo)
      datos.append("archivo", archivo)
      return pedir(`${base}/documentos`, { method: "POST", body: datos })
    },
    onSuccess: refrescar,
  })
  const retirar = useMutation({
    mutationFn: (id: number) => pedir(`${base}/documentos/${id}`, { method: "DELETE" }),
    onSuccess: refrescar,
  })

  const catalogo = tipos.data?.tipos[dueno] ?? {}
  const obligatorios = new Set(tipos.data?.obligatorios[dueno] ?? [])
  const lista = docs.data ?? []
  const ver = (d: Documento) =>
    setViendo({ ruta: `${base}/documentos/${d.id}/archivo`, nombre: d.nombre_original })

  return (
    <div className="space-y-2">
      {!soloLectura && (
        <ul
          className="divide-y rounded-[var(--radius-control)] border"
          style={{ borderColor: "var(--border)" }}
        >
          {Object.entries(catalogo).map(([tipo, nombre]) => {
            const suyos = lista.filter((d) => d.tipo === tipo)
            return (
              <li key={tipo} className="flex flex-wrap items-start gap-2 p-2 text-sm">
                {suyos.length > 0 ? (
                  <CheckCircle2
                    size={16}
                    aria-label="Cargado"
                    style={{ color: "var(--success)" }}
                  />
                ) : (
                  <Circle
                    size={16}
                    aria-label="Pendiente"
                    style={{ color: obligatorios.has(tipo) ? "var(--warning)" : "var(--fg-muted)" }}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {nombre}
                    {obligatorios.has(tipo) && (
                      <span
                        className="ml-1 text-xs font-normal"
                        style={{ color: "var(--warning)" }}
                      >
                        · obligatorio para emitir
                      </span>
                    )}
                  </p>
                  {suyos.map((d) => (
                    <span key={d.id} className="mt-1 flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        className="flex items-center gap-1 underline-offset-2 hover:underline"
                        onClick={() => ver(d)}
                      >
                        <Eye size={12} aria-hidden={true} /> {d.nombre_original} ·{" "}
                        {comoFecha(d.creado_en)}
                      </button>
                      <button
                        type="button"
                        aria-label={`Retirar ${d.nombre_original}`}
                        onClick={() => retirar.mutate(d.id)}
                        style={{ color: "var(--destructive)" }}
                      >
                        <Trash2 size={12} aria-hidden={true} />
                      </button>
                    </span>
                  ))}
                </div>
                <BotonArchivo
                  accept={tipo === "LOGO" ? "image/png,image/jpeg,image/webp" : ACEPTA}
                  etiqueta={`Subir ${nombre}`}
                  disabled={subir.isPending}
                  onArchivo={(archivo) => subir.mutate({ tipo, archivo })}
                >
                  <Upload size={14} aria-hidden={true} /> Subir
                </BotonArchivo>
              </li>
            )
          })}
        </ul>
      )}
      {soloLectura && (
        <ul className="space-y-1 text-sm">
          {lista.length === 0 && <li style={{ color: "var(--fg-muted)" }}>Sin documentos.</li>}
          {lista.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                className="flex items-center gap-1 underline-offset-2 hover:underline"
                onClick={() => ver(d)}
              >
                <Eye size={12} aria-hidden={true} /> {d.dueno.toLowerCase()} · {d.tipo} ·{" "}
                {d.nombre_original}
              </button>
            </li>
          ))}
        </ul>
      )}
      {(subir.error ?? retirar.error) && (
        <p role="alert" className="text-xs" style={{ color: "var(--destructive)" }}>
          {(subir.error ?? retirar.error) instanceof ErrorApi
            ? (subir.error ?? retirar.error)?.message
            : "No se pudo guardar el documento"}
        </p>
      )}
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </div>
  )
}
